import { defineComponent as _defineComponent } from 'vue'
import { computed, PropType, ref } from 'vue';
import InfoBlock, { InformationBlock } from 'ah-common-lib/src/common/components/InfoBlock.vue';
import { CompoundValidation, FormEvent, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { setState, toDataModel } from 'ah-common-lib/src/form/helpers';
import { checkAddressHistoryValidity, IndividualType, OnboardingIndividualInfo } from 'ah-api-gateways';
import ProposedUserPermissionsForm from '@/app/components/registration/forms/company/ProposedUserPermissionsForm.vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import {
  AdditionalUserFormObj,
  useRegistrationIndividuals,
} from '../../registration/forms/company/useRegistrationIndividuals';
import { useToast } from 'ah-common-lib/src/toast';
import { useTheme } from 'ah-theme';
import { helpers } from '@vuelidate/validators';
import { makeMemberForm } from './reviewForms';
import RemoveButton from '../../registration/forms/common/RemoveButton.vue';
import ClientMemberInfoBlock from './ClientMemberInfoBlock.vue';
import RegistrationAddressForm from '../../registration/forms/common/RegistrationAddressForm.vue';
import ReviewScreenPreviousAddressesForms from './ReviewScreenPreviousAddressesForms.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ClientMembersReview',
  props: {
  editable: {
    type: Boolean,
    default: true,
  },
  model: {
    type: Array as PropType<OnboardingIndividualInfo[]>,
    required: true,
  },
},
  emits: ["update:model"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:model', value: OnboardingIndividualInfo[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const theme = useTheme();




const requestManager = useRequestManager().manager;

const individuals = computed(() => props.model);

const toast = useToast();

const primaryOwnerAddressValidation = ref<FormValidation>();
const primaryOwnerPreviousAddressesValidation = ref<CompoundValidation>();

const secondaryOwnerAddressValidation = ref<FormValidation>();
const secondaryOwnerPreviousAddressesValidation = ref<CompoundValidation>();

const differentFromIndividuals = helpers.withParams(
  { type: 'differentFromIndividuals' },
  (val: any) => !registrationIndividuals.isDuplicatedEmail(val)
);

const differentFromApplicant = helpers.withParams(
  { type: 'differentFromApplicant' },
  (val: any) => registrationIndividuals.applicantEmail.value !== val
);

const differentFromOwner = helpers.withParams(
  { type: 'differentFromOwner' },
  (val: any) => registrationIndividuals.ownerEmail.value !== val
);

const differentFromSecondaryOwner = helpers.withParams(
  { type: 'differentFromSecondaryOwner' },
  (val: any) => registrationIndividuals.secondaryOwnerEmail.value !== val
);

function ownerEmailValidations() {
  return { differentFromApplicant, differentFromIndividuals, differentFromSecondaryOwner };
}

function secondaryOwnerEmailValidations() {
  return { differentFromApplicant, differentFromIndividuals, differentFromOwner };
}

function otherEmailValidations() {
  return { differentFromApplicant, differentFromIndividuals, differentFromSecondaryOwner, differentFromOwner };
}

const registrationIndividuals = useRegistrationIndividuals({
  requestManager,
  individuals,
  userFormFactory: (individual) => {
    if (individual?.owner) {
      return makeMemberForm(true, ownerEmailValidations(), true);
    } else if (individual?.secondaryOwner) {
      return makeMemberForm(true, secondaryOwnerEmailValidations(), false);
    }
    return makeMemberForm(false, otherEmailValidations(), false);
  },
});

const editing = ref(false);
const showErrorMessage = ref(false);

const partnerName = computed(() => theme?.val?.name || '');

const applicantIsLabel = computed(() =>
  registrationIndividuals.isApplicantOwner.value ? 'Authorised Signatory' : 'Employee'
);

const isPrimaryOwnerUKResident = computed(
  () => registrationIndividuals.owner.value?.individual?.currentAddress?.countryCode === 'GB'
);

const isSecondaryOwnerUKResident = computed(
  () => registrationIndividuals.secondaryOwner.value?.individual?.currentAddress?.countryCode === 'GB'
);

const representativeBlock = ref<InformationBlock[]>([
  {
    label: `${registrationIndividuals.applicant.value?.individual.firstName ?? 'Applicant'} is...`,
    key: 'is',
    value: applicantIsLabel.value,
    cols: 4,
  },
  {
    label: 'Requested Permissions',
    key: 'permissions',
    value:
      registrationIndividuals.applicant.value?.individual.type === IndividualType.CLIENT_ADMIN ? 'Superuser' : 'Custom',
    cols: 4,
  },
]);

const userBlock = ref<InformationBlock[]>([
  { label: 'Title', key: 'title', cols: 4 },
  { label: 'Name', key: 'firstName', cols: 4 },
  { label: 'Surname', key: 'lastName', cols: 4 },
  { label: 'Email Address', key: 'email', cols: 4 },
  { label: 'Mobile Number', key: 'phoneNumber', cols: 4 },
  { label: 'Date of Birth', key: 'birthDate', cols: 4 },
  { label: 'Requested Permissions', key: 'permissions', cols: 4 },
]);

const isEditable = computed(() => props.editable !== false);

const areFormsInvalid = computed(() => {
  const isPrimaryOwnerInvalid =
    !!primaryOwnerAddressValidation.value?.$invalid || !!primaryOwnerPreviousAddressesValidation.value?.$invalid;

  let isSecondaryOwnerInvalid = false;
  if (registrationIndividuals.secondaryOwner.value) {
    isSecondaryOwnerInvalid =
      !!secondaryOwnerAddressValidation.value?.$invalid || !!secondaryOwnerPreviousAddressesValidation.value?.$invalid;
  }

  const isRegistrationIndividualsInvalid = !!registrationIndividuals.validation.value?.$invalid;

  return isPrimaryOwnerInvalid || isSecondaryOwnerInvalid || isRegistrationIndividualsInvalid;
});

function checkOwnerAddressValidity(individual?: OnboardingIndividualInfo) {
  const isUKResident = individual?.currentAddress?.countryCode === 'GB';
  if (!isUKResident || !individual) {
    return true;
  }

  const addressHistoryValidity = checkAddressHistoryValidity({
    currentAddress: { ...individual?.currentAddress },
    previousAddresses: individual?.previousAddresses || [],
  });
  return addressHistoryValidity.age && addressHistoryValidity.gaps;
}

function save() {
  registrationIndividuals.submitForms(registrationIndividuals.additionalUsers.value);
  if (areFormsInvalid.value) {
    toast.error('Form contains errors! Please review and resubmit.');
    return;
  }

  if (
    !checkOwnerAddressValidity(registrationIndividuals.owner.value?.individual) ||
    !checkOwnerAddressValidity(registrationIndividuals.secondaryOwner.value?.individual)
  ) {
    showErrorMessage.value = true;
    toast.error('Please review the address history. Ensure it spans at least 3 years with no gaps for both owners.');
    return;
  }

  registrationIndividuals.saveEntries().subscribe(
    () => {
      editing.value = false;
      showErrorMessage.value = false;
      emit(
        'update:model',
        registrationIndividuals.individualEntries.value.map((i) => i.individual)
      );
      toast.success('Changes updated successfully.');
    },
    () => {
      toast.error('Please review addresses. UK residents must provide addresses spanning back 3 years, with no gaps.');
      showErrorMessage.value = true;
    }
  );
}

function hasAnyPermissionsSet(individual: OnboardingIndividualInfo) {
  return (individual.proposedPermissions?.findIndex((p) => p.allow) ?? -1) >= 0;
}

function onUserFormEvent(event: FormEvent, userForm: AdditionalUserFormObj) {
  if (event.event === 'form-field-set-value') {
    setState(userForm.form, 'errors', [], true);

    registrationIndividuals.updateEntry(
      {
        ...userForm.individual,
        ...toDataModel(userForm.form),
      },
      userForm
    );
  }
}

function toggleEditing() {
  editing.value = !editing.value;

  if (!editing.value) {
    registrationIndividuals.resetEntries();
  }
}

return { __sfc: true,theme, props, emit, requestManager, individuals, toast, primaryOwnerAddressValidation, primaryOwnerPreviousAddressesValidation, secondaryOwnerAddressValidation, secondaryOwnerPreviousAddressesValidation, differentFromIndividuals, differentFromApplicant, differentFromOwner, differentFromSecondaryOwner, ownerEmailValidations, secondaryOwnerEmailValidations, otherEmailValidations, registrationIndividuals, editing, showErrorMessage, partnerName, applicantIsLabel, isPrimaryOwnerUKResident, isSecondaryOwnerUKResident, representativeBlock, userBlock, isEditable, areFormsInvalid, checkOwnerAddressValidity, save, hasAnyPermissionsSet, onUserFormEvent, toggleEditing, InfoBlock, IndividualType, ProposedUserPermissionsForm, RemoveButton, ClientMemberInfoBlock, RegistrationAddressForm, ReviewScreenPreviousAddressesForms }
}

})